import type { ProcedureStep, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import { ProcedureStepCompletionStatus } from "../../../enums/procedures"
import {
	ProcedureStepCompletionStatusEqualsFilterModifier,
	ProcedureStepCompletionStatusExcludesFilterModifier,
	ProcedureStepCompletionStatusIncludesFilterModifier,
	ProcedureStepCompletionStatusNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProcedureStepCompletionStatusFilterCell } from "./ProcedureStepCompletionStatusFilterCell"

type ProcedureStepCompletionStatusFieldValue = ProcedureStepCompletionStatus
type ProcedureStepCompletionStatusFilterValue = ProcedureStepCompletionStatus[] | ProcedureStepCompletionStatus

export class ProcedureStepCompletionStatusFilterDef extends BaseFilterDef<
	ProcedureStepCompletionStatusFieldValue,
	ProcedureStepCompletionStatusFilterValue
> {
	readonly id: string
	readonly defaultFilterValue: ProcedureStepCompletionStatusFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly procedureStep: Stored<ProcedureStep>

	constructor(procedureStep: Stored<ProcedureStep>) {
		super()
		this.procedureStep = procedureStep
		this.id = `procedure-step-completion-status:${procedureStep.uuid}`
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: ProcedureStepCompletionStatusEqualsFilterModifier,
			isValueValid: (_value: ProcedureStepCompletionStatusFieldValue): _value is ProcedureStepCompletionStatus =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureStepCompletionStatusFilterValue,
			): filterValue is ProcedureStepCompletionStatus => !Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: ProcedureStepCompletionStatusNotEqualsFilterModifier,
			isValueValid: (_value: ProcedureStepCompletionStatusFieldValue): _value is ProcedureStepCompletionStatus =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureStepCompletionStatusFilterValue,
			): filterValue is ProcedureStepCompletionStatus => !Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: ProcedureStepCompletionStatusIncludesFilterModifier,
			isValueValid: (_value: ProcedureStepCompletionStatusFieldValue): _value is ProcedureStepCompletionStatus =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureStepCompletionStatusFilterValue,
			): filterValue is ProcedureStepCompletionStatus[] => Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: ProcedureStepCompletionStatusExcludesFilterModifier,
			isValueValid: (_value: ProcedureStepCompletionStatusFieldValue): _value is ProcedureStepCompletionStatus =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureStepCompletionStatusFilterValue,
			): filterValue is ProcedureStepCompletionStatus[] => Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProcedureStepCompletionStatusFilterDef>): ReactNode {
		return <ProcedureStepCompletionStatusFilterCell key={props.filter.id} {...props} />
	}
}
